/**
 * InitialModal.js
 * Talkroute
 *
 * Created by Vel Genov on 1/15/20
 * Copyright (c) 2018 Talkroute. All right reserved.
 */

import React from "react";
import { connect } from "react-redux";
import { Button, Checkbox } from "semantic-ui-react";
import Linkify from "react-linkify";

import { domClasses, domIds } from "@constants/testConstants";

import uiString from "../constants/uiString";
import {
  settingChanged,
  OUTGOING_PHONE_NUMBER_SELECTED,
  OUTGOING_TEXT_PHONE_NUMBER_SELECTED,
  IS_AGREED_TERMS_CHANGED,
} from "../actions/preferencesActions";
import WholeCoverModal from "../components/modal/WholeCoverModal";
import validateArray from "../helpers/arrayValidator";
import LocalStorageManager, {
  LOCALSTORAGE_ITEM_KEYS,
} from "@helpers/LocalStorageManager";
import ArrowIcon from "@resources/icons/arrow-down.svg"
import DropdownComponent from "../components/PresentationComponents/DropdownComponent";
import clsx from "clsx";
import { getTextMessageAllowedForNumber } from "@services/dataClasses/TalkroutePhoneNumber";
interface IInitialModalProps {
  outgoingPhoneNumbers: any[];
  outgoingTextPhoneNumbers: any[];
  selectedOutgoingPhoneNumber: string;
  selectedOutgoingTextNumber: string;
  isAgreedTerms: boolean;
  settingChanged: (name: string, value: any) => any;
}

const arrowLeft = {
  transform: "rotate(90deg)",
  width: "1.18em",
  height: "1em",
  opacity: 0.5,
  marginRight: 4,
}

const arrowRight = {
  transform: "rotate(-90deg)",
  width: "1.18em",
  height: "1em",
  opacity: 0.5,
  marginLeft: 4,
}
/**
 * A React component responsible for the first modal dialog
 */
class InitialModal extends React.Component<IInitialModalProps, any> {
  static defaultProps = {
    outgoingPhoneNumbers: [],
    outgoingTextPhoneNumbers: [],
    isAgreedTerms: false,
  };

  /**
   * Shows first welcome modal
   */
  showWelcome = () => {
    const { slideNum } = this.state;

    return (
      <WholeCoverModal>
        <div
          className="text-large"
          style={{
            borderBottom: "1px solid var(--color-dark-grey)",
            textAlign: "center",
            fontWeight: 700,
            padding: "20px 0 15px 0",
          }}
        >
          {uiString.initialModal.welcomeModal.TITLE}
        </div>
        <div className="text-large" style={{ padding: "15px 15px 0 15px" }}>
          {uiString.initialModal.welcomeModal.SUMMARY}
        </div>
        <div className="text-normal" style={{ padding: "15px 15px 0 15px" }}>
          {uiString.initialModal.welcomeModal.CONTENT}
        </div>
        <div className="text-normal" style={{ padding: "15px 30px 0 30px" }}>
          {uiString.initialModal.welcomeModal.FUNC_1}
        </div>
        <div className="text-normal" style={{ padding: "0 30px 0 30px" }}>
          {uiString.initialModal.welcomeModal.FUNC_2}
        </div>
        <div className="text-normal" style={{ padding: "0 30px 0 30px" }}>
          {uiString.initialModal.welcomeModal.FUNC_3}
        </div>
        <div className="text-normal" style={{ padding: "0 30px 0 30px" }}>
          {uiString.initialModal.welcomeModal.FUNC_4}
        </div>
        <div className="text-small" style={{ padding: "15px 15px 0 15px" }}>
          <Linkify>{uiString.initialModal.welcomeModal.DESC}</Linkify>
        </div>
        <div
          className="text-normal"
          style={{
            padding: "15px 15px 15px 15px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            className={clsx("initialModalButton", domClasses.MODAL_ACCEPT_BUTTON)}
            basic
            icon
            onClick={() => this.setState({ slideNum: slideNum + 1 })}
            style={{ margin: "0", display: "flex"}}
          >
            {uiString.initialModal.NEXT} <ArrowIcon style={arrowRight}/>
          </Button>
        </div>
      </WholeCoverModal>
    );
  };

  /**
   * Dispatch an action that a setting has changed
   *
   * @param name the name of the setting that change
   * @param value the value of the setting that changed
   */
  dispatchChangeAction = (name: string | undefined, value: unknown) => {
    const { settingChanged: settingChangedLocal } = this.props;
    settingChangedLocal(name as string, value);
  };

  /**
   * Shows an dropdown of phone numbers
   *
   * @param prefs the infos of dropdown: phone numbers, label, selected value, name
   */
  showPhoneNumberDropdown = (prefs) => {
    const { phoneNumbers, label, selectedValue, name } = prefs;
    let placeholderValue = uiString.SELECT_PHONE_NUMBER;
    let dropdownOptions: any = [];

    const editableTextPhoneNumbers = phoneNumbers.filter(
      (numberEntry) => getTextMessageAllowedForNumber(numberEntry.phoneNumber, phoneNumbers)
    );

    if (validateArray(phoneNumbers)) {
      dropdownOptions = editableTextPhoneNumbers.map((entry) => {
        return {
          value: entry.getPhoneNumber(),
          text: entry.getPhoneNumberFormatted(),
        };
      });
    } else {
      // Add "None" as an option only if there are no other phone numbers
      dropdownOptions.push({
        value: null,
        text: uiString.preferences.OUTGOING_NONE,
      });

      placeholderValue = uiString.preferences.OUTGOING_NONE;
    }

    return (
      <div style={{ padding: "0 0 0 10px" }}>
        <div style={{ padding: "5px 0" }}>{label}</div>
        <DropdownComponent
          className="text-light"
          value={selectedValue}
          name={name}
          placeholder={placeholderValue}
          options={dropdownOptions}
          onChange={e => this.dispatchChangeAction(e.target.name, e.target.value)}
          smallIcon 
        />
      </div>
    );
  };

  /**
   * Shows second Caller ID modal
   */
  showCallerID = () => {
    const { slideNum } = this.state;
    const {
      outgoingPhoneNumbers,
      selectedOutgoingPhoneNumber,
      outgoingTextPhoneNumbers,
      selectedOutgoingTextNumber,
    } = this.props;

    return (
      <WholeCoverModal>
        <div
          className="text-large"
          style={{
            borderBottom: "1px solid var(--color-dark-grey)",
            textAlign: "center",
            fontWeight: 700,
            padding: "20px 0 15px 0",
          }}
        >
          {uiString.initialModal.callerModal.TITLE}
        </div>
        <div className="text-large" style={{ padding: "15px 15px 0 15px" }}>
          {uiString.initialModal.callerModal.SUMMARY}
        </div>
        <div className="text-normal" style={{ padding: "15px 30px 0 30px" }}>
          {this.showPhoneNumberDropdown({
            phoneNumbers: outgoingPhoneNumbers,
            label: uiString.preferences.OUTGOING_CALLER_ID,
            selectedValue: selectedOutgoingPhoneNumber,
            name: OUTGOING_PHONE_NUMBER_SELECTED,
          })}
        </div>
        <div className="text-normal" style={{ padding: "0 30px 0 30px" }}>
          {this.showPhoneNumberDropdown({
            phoneNumbers: outgoingTextPhoneNumbers,
            label: uiString.preferences.OUTGOING_TEXT_MESSAGE_PHONE_NUMBER,
            selectedValue: selectedOutgoingTextNumber,
            name: OUTGOING_TEXT_PHONE_NUMBER_SELECTED,
          })}
        </div>
        <div className="text-small" style={{ padding: "15px 15px 0 15px" }}>
          {uiString.initialModal.callerModal.DESC}
        </div>
        <div
          className="text-normal"
          style={{
            padding: "15px 15px 15px 15px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            className={clsx("initialModalButton")}
            basic
            icon
            onClick={() => this.setState({ slideNum: slideNum - 1 })}
            style={{ margin: "0", display: "flex" }}
          >
            <ArrowIcon style={arrowLeft}/>
            {uiString.initialModal.BACK}
          </Button>
          <Button
            className={clsx("initialModalButton",domClasses.MODAL_ACCEPT_BUTTON)}
            basic
            icon
            onClick={() => this.setState({ slideNum: slideNum + 1 })}
            style={{ margin: "0", display: "flex",

           }}
          >
            {uiString.initialModal.NEXT}
            <ArrowIcon style={arrowRight}/>
          </Button>
        </div>
      </WholeCoverModal>
    );
  };

  /**
   * Shows third Terms & Conditions modal
   */
  showTermsConditions = () => {
    const { isAgreedTerms } = this.props;
    const { slideNum } = this.state;

    return (
      <WholeCoverModal>
        <div
          className="text-large"
          style={{
            borderBottom: "1px solid var(--color-dark-grey)",
            textAlign: "center",
            fontWeight: 700,
            padding: "20px 0 15px 0",
          }}
        >
          {uiString.initialModal.termsModal.TITLE}
        </div>
        <div
          className="text-large"
          style={{ padding: "15px 15px 0 15px", fontWeight: 700 }}
        >
          {uiString.initialModal.termsModal.SUMMARY}
        </div>
        <div className="text-small" style={{ padding: "15px 15px 0 15px" }}>
          {uiString.initialModal.termsModal.DESC_1}
          <b>{uiString.initialModal.termsModal.DESC_BOLD}</b>
          {uiString.initialModal.termsModal.DESC_2}
        </div>
        <Checkbox
          id={domIds.MODAL_TERMS_CHECKBOX}
          checked={isAgreedTerms}
          onClick={() => this.dispatchChangeAction(IS_AGREED_TERMS_CHANGED, !isAgreedTerms)}
          label={{children: <div className="text-small" style={{ paddingLeft: "1px" }}>
                {uiString.initialModal.termsModal.CHECK_DESC}
              </div>}}
          className="initial-dialog-checkbox"
        />
        <div
          className="text-normal"
          style={{
            padding: "15px 15px 15px 15px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            className={clsx("initialModalButton",domClasses.MODAL_ACCEPT_BUTTON)}
            basic
            icon
            onClick={() => this.setState({ slideNum: slideNum - 1 })}
            style={{ margin: "0", display: "flex" }}
          >
            <ArrowIcon style={arrowLeft}/>
            {uiString.initialModal.BACK}
          </Button>
          <Button
            className={domClasses.MODAL_ACCEPT_BUTTON}
            disabled={!isAgreedTerms}
            basic
            icon
            onClick={this.handleFinishFlow}
            style={{ margin: "0" }}
          >
            {uiString.initialModal.FINISH}
          </Button>
        </div>
      </WholeCoverModal>
    );
  };

  /**
   * Finish flow - hide the modal and save the option in local storage
   */
  handleFinishFlow = () => {
    this.setState({ isShowModal: false })
    this.localStorageManager.set("true");
  }

  /**
   * Shows modal content according to the slide number
   */
  showContent = () => {
    const { slideNum } = this.state;

    switch (slideNum) {
      case 0:
        return this.showWelcome();
      case 1:
        return this.showCallerID();
      case 2:
        return this.showTermsConditions();
      default:
        return this.showWelcome();
    }
  };

  localStorageManager: LocalStorageManager;
  constructor(props) {
    super(props);
    this.state = {
      slideNum: 0, // The number of current slide
      isShowModal: false, // Whether it shows modal or not
    };
    this.localStorageManager = new LocalStorageManager({
      key: LOCALSTORAGE_ITEM_KEYS.INITIAL_LOADER_CLOSED,
    });
  }

  componentDidMount() {
    const {
      isAgreedTerms,
      outgoingPhoneNumbers,
      outgoingTextPhoneNumbers,
    } = this.props;

    if (!isAgreedTerms) {
      // Set the outgoing number to the first item of options
      if (validateArray(outgoingPhoneNumbers)) {
        this.dispatchChangeAction(
          OUTGOING_PHONE_NUMBER_SELECTED,
          outgoingPhoneNumbers[0].getPhoneNumber()
        );
      }

      if (validateArray(outgoingTextPhoneNumbers)) {
        this.dispatchChangeAction(
          OUTGOING_TEXT_PHONE_NUMBER_SELECTED,
          outgoingTextPhoneNumbers[0].getPhoneNumber()
        );
      }

      this.setState({ isShowModal: true });
    }
  }

  componentDidUpdate(prevProps) {
    const {
      outgoingPhoneNumbers: prevOutgoingPhoneNumbers,
      outgoingTextPhoneNumbers: prevOutgoingTextPhoneNumbers,
    } = prevProps;
    const {
      isAgreedTerms,
      outgoingPhoneNumbers,
      outgoingTextPhoneNumbers,
    } = this.props;

    if (!isAgreedTerms) {
      // If new numbers are loaded from server,
      // set the outgoing number to the first item of options
      if (prevOutgoingPhoneNumbers.length !== outgoingPhoneNumbers.length) {
        this.dispatchChangeAction(
          OUTGOING_PHONE_NUMBER_SELECTED,
          outgoingPhoneNumbers[0].getPhoneNumber()
        );
      }

      if (
        prevOutgoingTextPhoneNumbers.length !== outgoingTextPhoneNumbers.length
      ) {
        this.dispatchChangeAction(
          OUTGOING_TEXT_PHONE_NUMBER_SELECTED,
          outgoingTextPhoneNumbers[0].getPhoneNumber()
        );
      }
    }
  }

  render() {
    const { isShowModal } = this.state;
    const areTermsAccepted = this.localStorageManager.get();

    if (isShowModal && areTermsAccepted) {
      return this.showContent();
    }

    return null;
  }
}

// Redux data binding
const mapStateToProps = (state) => ({
  outgoingPhoneNumbers: state.preferences.outgoingPhoneNumbers,
  outgoingTextPhoneNumbers: state.preferences.outgoingTextPhoneNumbers,
  selectedOutgoingPhoneNumber: state.preferences.selectedOutgoingPhoneNumber,
  selectedOutgoingTextNumber: state.preferences.selectedOutgoingTextNumber,
  isAgreedTerms: state.preferences.isAgreedTerms,
});

export default connect(mapStateToProps, { settingChanged })(InitialModal);
